import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/**
 * Story 47.7 — internal style guide.
 *
 * Renders every theme primitive on a single page so we can spot drift
 * between the default Kairos theme and any tenant-applied overrides
 * without poking through 30 individual screens. Also serves as a sanity
 * check after a theme_tokens edit.
 */
export function StyleGuidePage() {
  return (
    <div className="mx-auto max-w-5xl space-y-8 p-6">
      <header>
        <h1 className="text-3xl font-semibold">Style guide</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every theme primitive on one page. Toggle light / dark from the
          sidebar to see the dark variant. Tenant overrides apply
          automatically.
        </p>
      </header>

      <Section title="Surface tiers">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>tier=&quot;default&quot; (surface-2)</CardTitle>
            </CardHeader>
            <p className="text-sm">Standard card on canvas.</p>
          </Card>
          <Card tier="raised">
            <CardHeader>
              <CardTitle>tier=&quot;raised&quot; (surface-3)</CardTitle>
            </CardHeader>
            <p className="text-sm">Modals, popovers, side panels.</p>
          </Card>
          <Card tier="sunken">
            <CardHeader>
              <CardTitle>tier=&quot;sunken&quot;</CardTitle>
            </CardHeader>
            <p className="text-sm">Alt rows, disabled blocks.</p>
          </Card>
        </div>
      </Section>

      <Section title="Color swatches">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Swatch token="--color-surface-1" label="canvas" />
          <Swatch token="--color-surface-2" label="card" />
          <Swatch token="--color-surface-3" label="raised" />
          <Swatch token="--color-surface-sunken" label="sunken" />
          <Swatch token="--color-on-surface" label="text" />
          <Swatch token="--color-on-surface-muted" label="muted text" />
          <Swatch token="--color-primary" label="primary" />
          <Swatch token="--color-accent" label="accent" />
          <Swatch token="--color-divider" label="divider" />
          <Swatch token="--color-destructive" label="destructive" />
        </div>
      </Section>

      <Section title="Buttons">
        <div className="flex flex-wrap items-center gap-3">
          <Button>Primary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button disabled>Disabled</Button>
          <Button size="sm">Small</Button>
        </div>
      </Section>

      <Section title="Inputs">
        <Card>
          <div className="space-y-3">
            <Input placeholder="Standard input" />
            <Input placeholder="Disabled" disabled />
            <textarea
              placeholder="Textarea"
              rows={3}
              className="w-full rounded-md border bg-background p-2 text-sm"
            />
            <select className="w-full rounded-md border bg-background px-3 py-2 text-sm">
              <option>Select option</option>
              <option>Other</option>
            </select>
          </div>
        </Card>
      </Section>

      <Section title="Badges and pills">
        <div className="flex flex-wrap gap-2">
          <span className="rounded-full bg-blue-900/40 px-3 py-1 text-xs font-medium text-blue-200">
            NEW
          </span>
          <span className="rounded-full bg-green-900/40 px-3 py-1 text-xs font-medium text-green-200">
            QUALIFIED
          </span>
          <span className="rounded-full bg-emerald-900/40 px-3 py-1 text-xs font-medium text-emerald-200">
            CONVERTED
          </span>
          <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
            DISCARDED
          </span>
          <span className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
            primary pill
          </span>
        </div>
      </Section>

      <Section title="Typography">
        <Card>
          <div className="space-y-2">
            <h1 className="text-3xl font-light">Display / H1 — 300 Light</h1>
            <h2 className="text-2xl font-medium">H2 — 500 Medium</h2>
            <h3 className="text-lg font-medium">H3 — 500 Medium</h3>
            <p className="text-base">Body — 400 Regular. Lopettia brand uses 300 Light here in marketing surfaces.</p>
            <p className="text-sm text-muted-foreground">Small — 400 Regular, muted.</p>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Label — 11px, +0.2em tracking
            </p>
          </div>
        </Card>
      </Section>

      <Section title="Table">
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Restaurant</th>
                <th className="px-4 py-2 text-left">Neighborhood</th>
                <th className="px-4 py-2 text-right">Rating</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2">Marmalade</td>
                <td className="px-4 py-2 text-muted-foreground">Old San Juan</td>
                <td className="px-4 py-2 text-right">4.6</td>
              </tr>
              <tr className="bg-muted/40">
                <td className="px-4 py-2">La Bombonera</td>
                <td className="px-4 py-2 text-muted-foreground">Old San Juan</td>
                <td className="px-4 py-2 text-right">4.3</td>
              </tr>
              <tr>
                <td className="px-4 py-2">Verde Mesa</td>
                <td className="px-4 py-2 text-muted-foreground">Old San Juan</td>
                <td className="px-4 py-2 text-right">4.6</td>
              </tr>
            </tbody>
          </table>
        </Card>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Swatch({ token, label }: { token: string; label: string }) {
  return (
    <div className="space-y-1">
      <div
        className="h-16 w-full rounded-md"
        style={{ backgroundColor: `var(${token})` }}
      />
      <div className="text-xs">
        <div className="font-medium">{label}</div>
        <div className="font-mono text-muted-foreground">{token}</div>
      </div>
    </div>
  );
}
